import styled from 'styled-components';
import { useRef, useState, useEffect } from 'react';

import { Icon } from 'components/icon';
import { formatPrice } from 'utils/helpers';
import {
  Payout,
  useRemoveManualMutation,
  useCorrectManualMutation,
} from 'store/api/payout';

import * as Styles from './subrows';
import { getPayoutItemSignedAmount } from './helpers';

const Rows = styled(Styles.SubRows)`
  button {
    border: 0;
    border-radius: 6px;
    cursor: pointer;
  }
  button:disabled {
    cursor: default;
    opacity: 0.45;
  }
  button:focus-visible {
    outline: 2px solid #245ac5;
    outline-offset: 2px;
  }
  button[title] {
    width: 28px;
    height: 28px;
    min-height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  button svg {
    width: 18px;
    height: 18px;
  }
  label {
    display: grid;
    gap: 5px;
    font-size: 14px;
  }
  input,
  textarea {
    width: 100%;
    border: 1px solid #cbd1df;
    border-radius: 6px;
    padding: 9px 10px;
    font: inherit;
    color: inherit;
    background: #fff;
  }
  textarea {
    min-height: 64px;
    resize: vertical;
  }
  .edit-actions button {
    padding: 8px 16px;
    color: #174eaf;
    background: #eaf0fb;
    font: inherit;
  }
  .edit-actions button:first-child {
    color: white;
    background: #1555c7;
  }
`;

export function ManualPayoutRows({
  rows,
  onBusy,
  editable,
  disabled,
  onRefresh,
  onFailure,
}: {
  rows: Payout[];
  editable: boolean;
  disabled: boolean;
  onFailure?: () => void;
  onRefresh: () => Promise<void>;
  onBusy: (value: boolean) => void;
}) {
  const [edit, setEdit] = useState<number>();
  // Remove asks first, as every other delete does (DeleteDialog's words),
  // inline: this list sits inside the Paycheck dialog (owner pass 17b).
  const [confirming, setConfirming] = useState<number>();
  // Focus goes to the question, and back to the entry's trash when it is
  // answered "Cancel" or with Escape (review 27 Oct).
  const confirmRef = useRef<HTMLDivElement>(null);
  const backTo = useRef<number | undefined>(undefined);
  const rowsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (confirming !== undefined) {
      confirmRef.current?.focus();
      return;
    }
    if (backTo.current !== undefined) {
      rowsRef.current
        ?.querySelector<HTMLButtonElement>(
          `[aria-label="Remove entry ${backTo.current}"]`
        )
        ?.focus();
      backTo.current = undefined;
    }
  }, [confirming]);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [save] = useCorrectManualMutation();
  const [remove] = useRemoveManualMutation();
  const finish = () => {
    setEdit(undefined);
    setConfirming(undefined);
    onBusy(false);
  };
  const act = async (item: Payout, deleting = false) => {
    if (working || disabled) {
      return;
    }
    setWorking(true);
    onBusy(true);
    setError('');
    try {
      if (deleting) {
        await remove(item.id).unwrap();
      } else {
        await save({ notes, id: item.id, amount: Number(amount) }).unwrap();
      }
      await onRefresh();
      finish();
    } catch (e: any) {
      onFailure?.();
      setError(
        e?.data?.message ||
          'Unable to save this correction. The saved entry is still shown.'
      );
    } finally {
      setWorking(false);
      if (deleting) {
        setConfirming(undefined);
        onBusy(false);
      }
    }
  };
  return (
    <Rows ref={rowsRef}>
      {rows.map((item) => (
        <div key={item.id} style={{ padding: 0 }}>
          <Styles.SubRow>
            <Styles.SubDescription>
              <span>{item.notes?.trim() ? item.notes : null}</span>
              <Styles.SubActions>
                {editable && item.canEditManual && (
                  <button
                    title="Edit"
                    type="button"
                    aria-label={`Edit entry ${item.id}`}
                    style={{
                      border: 0,
                      padding: 4,
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                    disabled={
                      disabled ||
                      working ||
                      edit !== undefined ||
                      confirming !== undefined
                    }
                    onClick={() => {
                      setEdit(item.id);
                      setAmount(String(item.amount));
                      setNotes(item.notes || '');
                      setError('');
                      onBusy(true);
                    }}
                  >
                    <Icon.Edit />
                  </button>
                )}
                {editable && item.canRemoveManual && (
                  <button
                    type="button"
                    title="Remove"
                    aria-label={`Remove entry ${item.id}`}
                    onClick={() => {
                      setConfirming(item.id);
                      setError('');
                      onBusy(true);
                    }}
                    style={{
                      border: 0,
                      padding: 4,
                      cursor: 'pointer',
                      background: 'transparent',
                    }}
                    disabled={
                      disabled ||
                      working ||
                      edit !== undefined ||
                      confirming !== undefined
                    }
                  >
                    <Icon.Delete />
                  </button>
                )}
              </Styles.SubActions>
            </Styles.SubDescription>
            <Styles.SubAmount $negative={getPayoutItemSignedAmount(item) < 0}>
              {getPayoutItemSignedAmount(item) >= 0 ? '+' : '−'}
              {formatPrice(Math.abs(getPayoutItemSignedAmount(item)))}
            </Styles.SubAmount>
          </Styles.SubRow>
          {confirming === item.id && (
            <div
              tabIndex={-1}
              ref={confirmRef}
              role="alertdialog"
              aria-label={`Remove entry ${item.id}?`}
              style={{ gap: 8, marginTop: 8, display: 'grid', outline: 'none' }}
              // Escape answers this question only: the Paycheck dialog (its
              // popup listens to keyup on the document) stays open.
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              onKeyUp={(event) => {
                if (event.key === 'Escape' && !working) {
                  event.stopPropagation();
                  backTo.current = item.id;
                  finish();
                }
              }}
            >
              <p style={{ margin: 0, fontSize: 14 }}>
                You are about to delete{' '}
                <span className="semibold">
                  {item.notes?.trim() ? item.notes.trim() : 'this entry'} (
                  {getPayoutItemSignedAmount(item) >= 0 ? '+' : '−'}
                  {formatPrice(Math.abs(getPayoutItemSignedAmount(item)))})
                </span>
                . Are you sure you want to do this?
              </p>
              <div className="edit-actions" style={{ gap: 8, display: 'flex' }}>
                <button
                  type="button"
                  disabled={disabled || working}
                  onClick={() => void act(item, true)}
                >
                  Delete
                </button>
                <button
                  type="button"
                  disabled={working}
                  onClick={() => {
                    backTo.current = item.id;
                    finish();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {edit === item.id && (
            <div style={{ gap: 8, marginTop: 8, display: 'grid' }}>
              <label>
                Amount
                <input
                  step="0.01"
                  type="number"
                  value={amount}
                  disabled={working}
                  aria-label="Entry amount"
                  onChange={(e) => setAmount(e.target.value)}
                />
              </label>
              <label>
                Description
                <textarea
                  value={notes}
                  disabled={working}
                  aria-label="Entry description"
                  onChange={(e) => setNotes(e.target.value)}
                />
              </label>
              <div className="edit-actions" style={{ gap: 8, display: 'flex' }}>
                <button
                  type="button"
                  onClick={() => void act(item)}
                  disabled={
                    disabled ||
                    working ||
                    !Number.isFinite(Number(amount)) ||
                    Number(amount) === 0
                  }
                >
                  Save
                </button>
                <button type="button" onClick={finish} disabled={working}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
      {error && <p role="alert">{error}</p>}
    </Rows>
  );
}
